import React, { useState, useEffect, useRef } from 'react';
import type { XYCoord } from 'react-dnd';
import { useDrop } from 'react-dnd';
import {
  Box,
  BoxProps,
  Button,
  HStack,
  IconButton,
  Image,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { DragItem, ItemTypes } from '../dnd/types';
import { DragBox } from '../dnd/DragBox';
import { DragLayer } from '../dnd/DragLayer';
import { dirFromDndPos, flexDirFromDndPos, getCoords } from '../shared/utils';
import { RefreshIcon } from '../../../../../../components/icons';

// vertical stack - one below the other, evenly spaced, no overlap
const getStackedPositionInSpot = (
  spot: Spot,
  spotIndex: number,
  totalSpots: number,
): { x: number; y: number } => {
  if (spot.points?.length === 2) {
    const minX = parseFloat(String(spot.points[0]!.x)) * 0.8;
    const maxX = parseFloat(String(spot.points[1]!.x)) * 0.8;
    const minY = parseFloat(String(spot.points[0]!.y));
    const maxY = parseFloat(String(spot.points[1]!.y));
    const x = minX + (maxX - minX) / 2;
    const y =
      minY + ((spotIndex + 1) / (totalSpots + 1)) * (maxY - minY);
    return { x, y };
  }
  if (spot.point) {
    return {
      x: parseFloat(String(spot.point.x)) * 0.8,
      y: parseFloat(String(spot.point.y)),
    };
  }
  return { x: 0, y: 0 };
};

type Point = {
  x: string | number;
  y: string | number;
};

type Spot = {
  key: string;
  type: 'Rectangle' | 'Circle' | 'Shape';
  point?: Point;
  radius?: string | number;
  points?: Point[];
  answer: string;
  caseSensitive?: string;
  showArea?: boolean;
};

interface ImageHotspotsDndProps extends BoxProps {
  src: string;
  altText?: string;
  spots: Spot[];
  debug?: boolean;
  showCorrect?: boolean;
  dndPosition?: 'left' | 'top' | 'right' | 'bottom' | 'string';
  isViewOnly?: boolean;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: ContainerState['state'];
}

export interface ContainerState {
  state: {
    [key: string]: {
      top?: number | string;
      left?: number | string;
      text: string;
      correct: boolean;
    };
  };
}

const ImageHotspotsDnd: React.FC<ImageHotspotsDndProps> = ({
  src,
  altText,
  spots,
  debug,
  showCorrect,
  dndPosition = 'right',
  isViewOnly,
  submit,
  reset,
  initialState,
  ...props
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [state, updateState] = useState<ContainerState['state']>({});
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const { t } = useTranslation();

  const announce = (msg: string) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(msg));
  };

  const moveBox = (
    id: string,
    left: number | string,
    top: number | string,
    width: number,
    height: number,
    reset?: boolean,
  ) => {
    const newState = { ...state };
    const spot = spots.find((s) => s.key === id);
    let correct = false;
    if (reset) {
      newState[id] = {
        text: newState[id]!.text,
        correct,
        left: undefined,
        top: undefined,
      };
      updateState(newState);
      return;
    }
    if (spot) {
      const leftNumber = parseFloat('' + left) + width / 2;
      const topNumber = parseFloat('' + top) + height / 2;
      const topLeftPoint = spot.points![0]!;
      const bottomRightPoint = spot.points![1]!;
      const topLeftPointLeft = parseFloat('' + topLeftPoint.x) * 0.8;
      const topLeftPointTop = parseFloat('' + topLeftPoint.y);
      const bottomRightPointLeft = parseFloat('' + bottomRightPoint.x) * 0.8;
      const bottomRightPointTop = parseFloat('' + bottomRightPoint.y);
      if (
        leftNumber >= topLeftPointLeft &&
        leftNumber <= bottomRightPointLeft &&
        topNumber >= topLeftPointTop &&
        topNumber <= bottomRightPointTop
      ) {
        correct = true;
      }
    }

    newState[id] = { text: newState[id]!.text, correct, left, top };
    updateState(newState);
  };

  const handlePickItem = (key: string) => {
    if (selectedItemId === key) {
      setSelectedItemId(null);
      announce('Deselected');
    } else {
      setSelectedItemId(key);
      announce(`Selected ${state[key]?.text}. Tab to a drop zone on the image and press Space or Enter to place it.`);
    }
  };

  const handleDropOnSpot = (spot: Spot) => {
    if (!selectedItemId) return;

    const tl = spot.points![0]!;
    const br = spot.points![1]!;
    const tlX = parseFloat(String(tl.x)) * 0.8;
    const tlY = parseFloat(String(tl.y));
    const brX = parseFloat(String(br.x)) * 0.8;
    const brY = parseFloat(String(br.y));

    // find keys of items already placed within this spot's bounds
    const existingInSpot = Object.keys(state).filter(k => {
      if (k === selectedItemId) return false;
      const item = state[k]!;
      if (item.left == null || item.top == null) return false;
      const lNum = parseFloat(String(item.left));
      const tNum = parseFloat(String(item.top));
      return lNum >= tlX && lNum <= brX && tNum >= tlY && tNum <= brY;
    });

    const allInSpot = [...existingInSpot, selectedItemId];
    const total = allInSpot.length;

    const newState = { ...state };
    allInSpot.forEach((key, idx) => {
      const { x: posX, y: posY } = getStackedPositionInSpot(spot, idx, total);
      newState[key] = {
        text: newState[key]!.text,
        correct: key === spot.key,
        left: `${posX}%`,
        top: `${posY}%`,
      };
    });

    updateState(newState);
    announce(`Placed on ${spot.answer || 'area'}.`);
    setSelectedItemId(null);
  };

  const handleReturnToPool = (key: string) => {
    moveBox(key, undefined as any, undefined as any, 0, 0, true);
    setSelectedItemId(key);
    announce(`${state[key]?.text} returned to pool and selected. Tab to a drop zone on the image and press Space or Enter to place it.`);
  };

  const [{ hovering }, dropRef] = useDrop(
    () => ({
      accept: ItemTypes.DragBox,
      drop(item: DragItem, monitor) {
        const delta = monitor.getDifferenceFromInitialOffset() as XYCoord;

        if (rootRef.current) {
          // const percentageDeltaX =
          //   (delta.x / rootRef.current.clientWidth) * 100;
          // const percentageDeltaY =
          //   (delta.y / rootRef.current.clientHeight) * 100;
          const width = (item.width / rootRef.current.clientWidth) * 100;
          const height = (item.height / rootRef.current.clientHeight) * 100;

          const parent = getCoords(rootRef.current);
          const child = getCoords(item.el);

          const left = `${
            ((child.left - parent.left + delta.x) /
              rootRef.current.clientWidth) *
            100
          }%`;
          const top = `${
            ((child.top - parent.top + delta.y) /
              rootRef.current.clientHeight) *
            100
          }%`;
          moveBox(item.id, left, top, width, height);
        }
        return undefined;
      },
      collect: (monitor) => ({
        hovering: monitor.isOver(),
      }),
    }),
    [moveBox],
  );

  const [, dropRef2] = useDrop(
    () => ({
      accept: ItemTypes.DragBox,
      drop(item: DragItem, monitor) {
        const delta = monitor.getDifferenceFromInitialOffset() as XYCoord;

        if (rootRef.current) {
          const width = (item.width / rootRef.current.clientWidth) * 100;
          const height = (item.height / rootRef.current.clientHeight) * 100;

          const parent = getCoords(rootRef.current);
          const child = getCoords(item.el);

          const left = `${
            ((child.left - parent.left + delta.x) /
              rootRef.current.clientWidth) *
            100
          }%`;
          const top = `${
            ((child.top - parent.top + delta.y) /
              rootRef.current.clientHeight) *
            100
          }%`;

          moveBox(item.id, left, top, width, height, true);
        }
        return undefined;
      },
    }),
    [moveBox],
  );

  const setInitialState = () => {
    const state: ContainerState['state'] = {};

    spots.forEach((spot, index) => {
      state[spot.key] = {
        text: spot.answer,
        correct: false,
      };
    });

    updateState(state);
  };

  useEffect(() => {
    if (initialState) {
      updateState(initialState);
      setShowResults(true);
      let correct = 0;

      Object.keys(initialState).forEach((key) => {
        if (initialState[key]!.correct) {
          correct += 1;
        }
      });
      setShownCorrect(correct);

      setShownCorrect(correct);
    } else setInitialState();
  }, [spots, initialState]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    Object.keys(state).forEach((key) => {
      if (state[key]!.correct) {
        correct += 1;
      }
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, spots.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    setSelectedItemId(null);
    if (reset != null) reset();
  };

  const isInteractive = !isViewOnly && !showResults;

  const ret = (
    <VStack
      w="full"
      spacing={0}
      gap={4}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && selectedItemId) {
          e.stopPropagation();
          setSelectedItemId(null);
          announce('Selection cancelled');
        }
      }}
    >
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
      >
        {announcement}
      </div>
      <Stack
        w="full"
        flexDir={flexDirFromDndPos(dndPosition)}
        {...props}
        ref={(el) => {
          dropRef(el);
          rootRef.current = el;
        }}
        position="relative"
      >
        <Image src={src} w="80%" h="auto" alt={altText ?? ''} />
        {spots.map((spot) => {
          const showDebugBg = debug || spot.showArea || showCorrect;
          const isTarget = !!selectedItemId && isInteractive;
          return (
            <Box
              key={spot.key}
              position="absolute"
              left={`${parseFloat(String(spot.points![0]!.x)) * 0.8}%`}
              top={spot.points![0]!.y}
              right={`${100 - parseFloat(String(spot.points![1]!.x)) * 0.8}%`}
              bottom={`${100 - parseFloat(String(spot.points![1]!.y))}%`}
              backgroundColor={showDebugBg ? 'rgba(16,46,142,0.15)' : isTarget ? 'rgba(255,255,255,0.15)' : undefined}
              borderRadius="4px"
              boxShadow={isTarget ? 'inset 0 0 0 2px white, inset 0 0 0 4px rgba(0,0,0,0.4)' : undefined}
              tabIndex={isTarget ? 0 : -1}
              role={isTarget ? 'button' : undefined}
              aria-label={`Drop zone: ${spot.answer}`}
              sx={{
                '&:focus-visible': {
                  outline: 'none',
                  backgroundColor: 'rgba(255,220,0,0.65)',
                  boxShadow: 'inset 0 0 0 3px var(--stemi-focus-inset-color)',
                  borderRadius: '4px',
                },
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  handleDropOnSpot(spot);
                }
              }}
            />
          );
        })}
        {showCorrect &&
          spots.map((spot, spotIndex) => {
            const { x: posX, y: posY } = getStackedPositionInSpot(
              spot,
              spotIndex,
              spots.length,
            );
            return (
              <Box
                key={`correct-${spot.key}`}
                position="absolute"
                left={`${posX}%`}
                top={`${posY}%`}
                transform="translate(-50%, -50%)"
                px={2}
                py={1}
                bg="white"
                borderRadius="md"
                boxShadow="md"
                fontSize="sm"
                fontWeight="medium"
                border="1px solid"
                borderColor="blue.200"
                pointerEvents="none"
              >
                {spot.answer}
              </Box>
            );
          })}
        <VStack
          spacing={0}
          gap={4}
          flexGrow={1}
          m="0 auto"
          position="static"
          background={hovering ? 'rgba(208,208,208,0.45)' : undefined}
          borderRadius="8px"
          direction={dirFromDndPos(dndPosition)}
          w={dirFromDndPos(dndPosition) === 'row' ? 'full' : undefined}
          minH={dirFromDndPos(dndPosition) === 'row' ? '40px' : undefined}
          minW={dirFromDndPos(dndPosition) === 'column' ? '120px' : undefined}
          align="center"
          alignSelf={
            dirFromDndPos(dndPosition) === 'column' ? 'stretch' : undefined
          }
          ref={dropRef2}
        >
          <DragLayer />
          {/* So the stack streches */}
          <div></div>
          {Object.keys(state).map((key) => {
            const { left, top, text } = state[key] as {
              top?: number;
              left?: number;
              text: string;
            };
            const isPlaced = left != null && top != null;
            return (
              <DragBox
                key={key}
                id={key}
                left={left}
                top={top}
                text={text}
                boxShadow="md"
                onKeyboardSelect={
                  isInteractive
                    ? isPlaced
                      ? selectedItemId === null ? () => handleReturnToPool(key) : undefined
                      : (selectedItemId === null || selectedItemId === key) ? () => handlePickItem(key) : undefined
                    : undefined
                }
                isKeyboardSelected={selectedItemId === key}
                noDrag={!isInteractive}
              />
            );
          })}
        </VStack>
      </Stack>
      <HStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <>
            {showResults && (
              <IconButton
                size="sm"
                colorScheme="stemiBlue"
                variant="ghost"
                aria-label="reset"
                icon={<RefreshIcon boxSize="24px" />}
                onClick={_reset}
              >
                {t('general.reset')}
              </IconButton>
            )}
            <HStack>
              <span>
                {shownCorrect} / {spots.length}
              </span>
            </HStack>
          </>
        )}
        <HStack>
          {!isViewOnly && (
            <Button colorScheme="blue" onClick={_submit}>
              {t('general.submit')}
            </Button>
          )}
        </HStack>
      </HStack>
    </VStack>
  );

  if (debug) {
    return (
      <VStack>
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default ImageHotspotsDnd;